<script lang="ts">
import { defineComponent as DO_defineComponent } from 'vue';
export default /*#__PURE__*/ DO_defineComponent({
	name: 'ToParam'
});
</script>
<template>
	<div ref="paramRef" :flex="flex ? flex : null" class="to-param" :class="setClass">
		<div class="to-param-list">
			<template v-for="(item, index) in rowData" :key="index">
				<ParamItem :label="item.label" :value="item.value" :class="[item.merge ? 'merge-' + item.merge : '']" :tip="tip ? item.value : ''" :label-width="labelWidth" :wrap="item.wrap">
					<template #label>
						<slot name="label" :label="item.label" :value="item.value"></slot>
					</template>
					<slot :name="item.label" :value="item.value"></slot>
				</ParamItem>
				<div v-if="index < rowData.length - 1" class="to-param-divider"></div>
			</template>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed, ref, watch, onMounted, onBeforeUnmount, provide } from 'vue'
import { paramProps } from './param'
import ParamItem from './ParamItem.vue'



const props = defineProps(paramProps)
const rowData = ref<any[]>([])
const labelWidth = ref<string>(String(props.labelWidth))
const paramItems = ref<any[]>([])

// 提供给 ParamItem 组件的注册方法
const registerParamItem = (item: any) => {
	paramItems.value.push(item)
}

// 提供给 ParamItem 组件的注销方法
const unregisterParamItem = (item: any) => {
	const index = paramItems.value.indexOf(item)
	if (index !== -1) {
		paramItems.value.splice(index, 1)
	}
}

const getLabelWidth = () => {
	return labelWidth.value
}

// 提供给子组件
provide('toParamContext', {
	registerParamItem,
	unregisterParamItem,
	getLabelWidth
})

// 计算属性
const setClass = computed(() => {
	const arr = [props.col ? 'col-' + props.col : '', props.avg ? 'is-avg' : '', props.wrap ? 'is-wrap' : '']
	if (props.dir === 'v') {
		arr.push('dir-v')
	}
	if (props.dir === 'vo') {
		arr.push('dir-vo')
	}
	if (props.align) {
		arr.push(`align-${props.align}`)
	}
	if (props.mode) {
		arr.push(`mode-${props.mode}`)
	}
	if (props.type === 'v') {
		arr.push('dir-v')
		arr.push('align-center')
		arr.push('col-avg')
	}
	if (props.type === 'vo') {
		arr.push('dir-vo')
		arr.push('align-center')
		arr.push('col-avg')
	}

	return arr
})

// 修改getRowData方法，支持处理字符串格式的数据
const getRowData = () => {
	rowData.value = []

	// 如果data是字符串格式，则转换为数组格式
	if (typeof props.data === 'string') {
		const items = props.data.split(';').filter(item => item.trim() !== '')
		rowData.value = items.map(item => {
			const [label, value] = item.split(':')
			return {
				label: label ? label.trim() : '',
				value: value ? value.trim() : ''
			}
		})
	}

	// 如果data是数组格式，则直接使用
	else if (Array.isArray(props.data)) {
		rowData.value = [...props.data]
	}
}

watch(
	() => props.data,
	() => {
		if (props.data) {
			getRowData()
		}
	},
	{ deep: true, immediate: true }
)

// 计算最长label宽度的方法
const adjustLabelWidth = () => {
	requestAnimationFrame(() => {
		let maxWidth = 0
		paramItems.value.forEach(item => {
			if (item.vnode?.el?.querySelector('.label span')) {
				const labelSpan = item.vnode.el.querySelector('.label span')
				const labelWidth = labelSpan.getBoundingClientRect().width
				if (maxWidth < labelWidth) {
					maxWidth = labelWidth
				}
			}
		})

		// 直接设置width值，避免多次DOM操作
		labelWidth.value = maxWidth + 20 + 'px'
	})
}

// 监听labelWidth变化
watch(
	() => props.labelWidth,
	newVal => {
		labelWidth.value = String(newVal)
		if (newVal === 'compute') {
			adjustLabelWidth()
		}
	}
)

// 创建一个ref引用，用于获取组件根元素
const paramRef = ref(null)

// 使用ResizeObserver监听元素大小变化
let resizeObserver = null

onMounted(() => {
	if (props.labelWidth === 'compute') {
		// 创建ResizeObserver实例
		resizeObserver = new ResizeObserver(() => {
			adjustLabelWidth()
		})

		// 观察组件根元素
		if (paramRef.value) {
			resizeObserver.observe(paramRef.value)
		}

		// 初始计算
		adjustLabelWidth()
	}
})

onBeforeUnmount(() => {
	// 清理ResizeObserver
	if (resizeObserver) {
		resizeObserver.disconnect()
		resizeObserver = null
	}
})

defineExpose({
	getLabelWidth
})
</script>
